import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { CountrySelect } from '../common/CountrySelect';
import { IndustrySelect } from '../common/IndustrySelect';
import type { Organisation, SupplierCompanyProfile } from '../../types/types';

interface SupplierProfileBuilderStepGeneralProps {
  organisation?: Organisation;
  profile: SupplierCompanyProfile;
  setProfile: React.Dispatch<React.SetStateAction<SupplierCompanyProfile>>;
  errors: { [key: string]: string };
  setChangesSaved: React.Dispatch<React.SetStateAction<boolean>>;
}

const SupplierProfileBuilderStepGeneral: React.FC<
  SupplierProfileBuilderStepGeneralProps
> = ({ organisation, profile, setProfile, errors, setChangesSaved }) => {
  return (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Company Name</Form.Label>
            <Form.Control
              value={profile.company_name || organisation?.name || ''}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  company_name: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
            {errors.company_name && (
              <Form.Text className="text-danger">
                {errors.company_name}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Country of headquarters</Form.Label>
            <CountrySelect
              full
              onChange={(countryId) => {
                setProfile({
                  ...profile,
                  headquarters_location: countryId as string,
                });
                setChangesSaved(false);
              }}
              value={String(profile.headquarters_location || '')}
            />
            {errors.headquarters_location && (
              <Form.Text className="text-danger">
                {errors.headquarters_location}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Registration Number</Form.Label>
            <Form.Control
              value={
                profile.registration_number ||
                organisation?.registration_number ||
                ''
              }
              onChange={(e) => {
                setProfile({
                  ...profile,
                  registration_number: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
            {errors.registration_number && (
              <Form.Text className="text-danger">
                {errors.registration_number}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Company Website</Form.Label>
            <Form.Control
              value={profile.website || ''}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  website: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>D-U-N-S Number</Form.Label>
            <Form.Control
              value={profile.duns_number || ''}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  duns_number: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>NACE Industry</Form.Label>
            <IndustrySelect
              full
              onChange={(industryId) => {
                setProfile({
                  ...profile,
                  industry_id: industryId as string,
                });
                setChangesSaved(false);
              }}
              value={profile.industry_id as string}
            />
            {errors.industry_id && (
              <Form.Text className="text-danger">
                {errors.industry_id}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Company Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={profile.description || ''}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  description: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col>
          <div>
            <Form.Check
              type="switch"
              label="Display assessment results"
              id="display-assessment-results-switch"
              checked={profile.display_assesment_results || false}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  display_assesment_results: e.target.checked,
                });
                setChangesSaved(false);
              }}
            />
            <Form.Text className="text-muted">
              If enabled, buyers can see your assessment reports on your public
              profile.{' '}
              <u>
                Note that only reports that are PUBLISHED will be displayed.
              </u>
            </Form.Text>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default SupplierProfileBuilderStepGeneral;
